import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { Star } from 'lucide-react';
import styles from './KnowledgeArticle.module.css';

interface ArticleMedia {
  id: number;
  mediaType: string;
  url: string;
  caption?: string;
  displayOrder: number;
}

interface Article {
  id: number;
  slug: string;
  title: string;
  content: string;
  htmlContent?: string;
  category: string;
  skillLevel?: string;
  viewCount: number;
  tags?: string[];
  createdAt: string;
  updatedAt: string;
  lastReviewedAt?: string;
  averageRating: number;
  ratingCount: number;
  cardImageUrl?: string;
  author: {
    id: number;
    username: string;
    discordName?: string;
  };
  media?: ArticleMedia[];
}

const KnowledgeArticle: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userRating, setUserRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);

  useEffect(() => {
    const fetchArticle = async () => {
      if (!slug) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const response = await axios.get<Article>(`knowledgebase/articles/${slug}`);
        setArticle(response.data);

        // Fetch user's rating if logged in
        if (user) {
          try {
            const ratingResponse = await axios.get(`knowledgebase/articles/${response.data.id}/rating`);
            setUserRating(ratingResponse.data.userRating || 0);
          } catch (err) {
            console.log('Could not fetch user rating');
          }
        }
      } catch (err: any) {
        console.error('Error fetching article:', err);
        setError(err.response?.status === 404 ? 'Article not found' : 'Failed to load article');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug, user]);

  const getCategoryIcon = (category: string): string => {
    const iconMap: { [key: string]: string } = {
      flight: '✈️',
      mining: '⛏️',
      salvage: '🔧',
      cargo: '📦',
      'ground-ops': '🎯',
      medical: '⚕️',
      sop: '📋',
      training: '🎓',
    };
    return iconMap[category.toLowerCase()] || '📄';
  };

  const getSkillLevelBadge = (level?: string) => {
    if (!level) return null;
    const badgeClass = level.toLowerCase();
    return <span className={`${styles.badge} ${styles[badgeClass]}`}>{level}</span>;
  };

  const handleRating = async (rating: number) => {
    if (!user || !article) return;

    try {
      const response = await axios.post(`knowledgebase/articles/${article.id}/rate`, { rating });
      setUserRating(rating);
      
      // Update article with new average
      setArticle({
        ...article,
        averageRating: response.data.averageRating,
        ratingCount: response.data.ratingCount
      });
    } catch (err) {
      console.error('Failed to rate article:', err);
      alert('Failed to submit rating. Please try again.');
    }
  };

  const renderStars = () => {
    if (!article) return null;

    return (
      <div className={styles.ratingSection}>
        <div className={styles.ratingDisplay}>
          <div className={styles.averageRating}>
            <span className={styles.ratingNumber}>{article.averageRating.toFixed(1)}</span>
            <div className={styles.stars}>
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  size={20}
                  fill={star <= Math.round(article.averageRating) ? 'var(--warning-color)' : 'none'}
                  stroke={star <= Math.round(article.averageRating) ? 'var(--warning-color)' : 'var(--text-muted)'}
                />
              ))}
            </div>
            <span className={styles.ratingCount}>({article.ratingCount} {article.ratingCount === 1 ? 'rating' : 'ratings'})</span>
          </div>
        </div>

        {user && (
          <div className={styles.userRating}>
            <span className={styles.ratingLabel}>Your rating:</span>
            <div className={styles.stars}>
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  size={24}
                  className={styles.starButton}
                  fill={(hoverRating || userRating) >= star ? 'var(--warning-color)' : 'none'}
                  stroke={(hoverRating || userRating) >= star ? 'var(--warning-color)' : 'var(--text-muted)'}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => handleRating(star)}
                  style={{ cursor: 'pointer' }}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMedia = () => {
    if (!article?.media || article.media.length === 0) return null;

    return (
      <div className={styles.mediaSection}>
        {article.media.map((media) => {
          if (media.mediaType === 'video') {
            // Support YouTube/Vimeo embeds
            const videoUrl = media.url.includes('youtube.com') || media.url.includes('youtu.be')
              ? media.url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')
              : media.url;
            
            return (
              <div key={media.id} className={styles.mediaItem}>
                <iframe
                  src={videoUrl}
                  title={media.caption || 'Video'}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className={styles.videoEmbed}
                />
                {media.caption && <p className={styles.mediaCaption}>{media.caption}</p>}
              </div>
            );
          } else if (media.mediaType === 'image' || media.mediaType === 'diagram') {
            return (
              <div key={media.id} className={styles.mediaItem}>
                <img src={media.url} alt={media.caption || 'Article media'} className={styles.mediaImage} />
                {media.caption && <p className={styles.mediaCaption}>{media.caption}</p>}
              </div>
            );
          }
          return null;
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className={styles.articleContainer}>
        <div className={styles.loading}>Loading article...</div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className={styles.articleContainer}>
        <div className={styles.error}>
          <h2>{error || 'Article not found'}</h2>
          <Link to="/knowledge" className={styles.backButton}>
            ← Back to Knowledge Base
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.articleContainer}>
      <div className={styles.breadcrumbs}>
        <Link to="/knowledge">Knowledge Base</Link>
        <span className={styles.separator}>›</span>
        <Link to={`/knowledge?category=${article.category}`}>
          {getCategoryIcon(article.category)} {article.category}
        </Link>
        <span className={styles.separator}>›</span>
        <span>{article.title}</span>
      </div>

      <article className={styles.article}>
        {article.cardImageUrl && (
          <div className={styles.articleBanner}>
            <img src={article.cardImageUrl} alt={article.title} />
          </div>
        )}

        <header className={styles.articleHeader}>
          <div className={styles.headerMeta}>
            <span className={styles.categoryBadge}>
              {getCategoryIcon(article.category)} {article.category}
            </span>
            {getSkillLevelBadge(article.skillLevel)}
          </div>

          <h1 className={styles.articleTitle}>{article.title}</h1>

          <div className={styles.articleInfo}>
            <div className={styles.authorInfo}>
              <span className={styles.author}>
                By {article.author.discordName || article.author.username}
              </span>
              <span className={styles.separator}>•</span>
              <span className={styles.date}>
                {new Date(article.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
              {article.updatedAt !== article.createdAt && (
                <>
                  <span className={styles.separator}>•</span>
                  <span className={styles.updated}>
                    Updated {new Date(article.updatedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                </>
              )}
            </div>
            <div className={styles.stats}>
              <span className={styles.views}>👁️ {article.viewCount} views</span>
            </div>
          </div>

          {article.tags && article.tags.length > 0 && (
            <div className={styles.tags}>
              {article.tags.map((tag, idx) => (
                <span key={idx} className={styles.tag}>
                  {tag}
                </span>
              ))}
            </div>
          )}
        </header>

        {renderMedia()}

        {renderStars()}

        <div
          className={styles.articleContent}
          dangerouslySetInnerHTML={{ __html: article.htmlContent || article.content }}
        />

        <footer className={styles.articleFooter}>
          <Link to="/knowledge" className={styles.backButton}>
            ← Back to Knowledge Base
          </Link>
          
          {article.lastReviewedAt && (
            <p className={styles.lastReviewed}>
              Last reviewed: {new Date(article.lastReviewedAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          )}
        </footer>
      </article>
    </div>
  );
};

export default KnowledgeArticle;
